import { useMemo, useState } from "react";
import useConfiguration from "./use-configuration";
import {
  AccessEntryInfo,
  AllTimeMetricsResponse,
  BaseResponse,
  CachedComplianceData,
  FilteredTimeArgs,
  FilteredTimeSubjectArgs,
  IntuneData,
  IntuneDeleteResponse,
  IntuneSaveResponse,
  TenantData,
  ItemResponse,
  ListResponse,
  MachineAtTime,
  MachineAtTimesArgs,
  MachineByStatusArgs,
  MachineHistoryArgs,
  MachineSubjectArgs,
  MultiFilter,
  ScalarResponse,
  SiteInfo,
  SubjectPackageArgs,
  SubjectStatusAtTime,
  SubjectStatusReasonSummary,
  SubjectStatusSubjectSummary,
  SubjectStatusSummary,
  SummarySubjectStatusArgs,
  TimeBoundMetricsResponse,
  TimeRangeArgs,
  TimeResponse,
  UploadKeyInfo,
  UserInfoResponse,
  UserToken,
  ValueResponse,
  ExchangeCode
} from "system/types/wireTypes";
import IMachineDetails from "system/types/interfaces/IMachineDetails";
import IPagedRuntimeHistoryResponse from "system/types/interfaces/IPagedRuntimeHistoryResponse";
import IMachineSubjectHistory from "system/types/interfaces/IMachineSubjectHistory";
import HttpMethods from "system/types/enums/HttpMethods";
import useHttpRequest from "./use-http-request";
import authentication from "system/libraries/authentication";
import {
  CommonIniResponse,
  CompanyIniResponse,
  CompanyPolicyVersion,
  CompanyPolicyVersionDetail,
  IniInfo,
  PolicyPreviewResponse,
  IRetryOptions,
  PackageConfigSection,
  ReplicateIniChanges,
  ReplicateIniResponse,
  UnconfiguredRules,
} from "system/types/interfaces";
import { IPackageIcon } from "system/types/interfaces/IIconData";
import { IVulnerability } from "system/types/interfaces";
import { IPackageInfo, IPackageCustomerSummary } from "system/types/interfaces/IPackageInfo";

const useApi = () => {
  const [auth] = useState(authentication);
  const { currentTenant } = useConfiguration();

  const apiUrl = useMemo<string>(() => (process.env.REACT_APP_BACKEND_URL ?? "") + "/api/v1.0/", []);

  const httpRequest = useHttpRequest(apiUrl, auth);

  return useMemo(
    () => ({
      auth,
      admin: {
        me: async (): Promise<UserInfoResponse> => {
          return await httpRequest(HttpMethods.get, "admin/me");
        },
        sites: async (): Promise<ListResponse<SiteInfo>> => {
          return await httpRequest(HttpMethods.get, "admin/sites");
        },
        deleteSite: async (id: number): Promise<BaseResponse> => {
          return await httpRequest(HttpMethods.delete, `admin/sites/${id}`);
        },
        addEditSite: async (site: SiteInfo): Promise<BaseResponse> => {
          if (!currentTenant) {
            throw new Error(`Api request was not sent, no tenant defined.`);
          }
          return await httpRequest(HttpMethods.post, `admin/sites?tenantId=${currentTenant.siteId}`, site);
        },
        uploadKeys: async (): Promise<ListResponse<UploadKeyInfo>> => {
          if (!currentTenant) {
            throw new Error(`Api request was not sent, no tenant defined.`);
          }

          return await httpRequest(HttpMethods.get, `admin/uploadkeys?tenantId=${currentTenant.siteId}`);
        },
        deleteUploadKey: async (key23: string): Promise<BaseResponse> => {
          if (!currentTenant) {
            throw new Error(`Api request was not sent, no tenant defined.`);
          }

          return await httpRequest(
            HttpMethods.delete,
            `admin/uploadkeys?key23=${encodeURIComponent(key23)}&tenantId=${currentTenant.siteId}`
          );
        },
        addUploadKey: async (key23: string): Promise<BaseResponse> => {
          if (!currentTenant) {
            throw new Error(`Api request was not sent, no tenant defined.`);
          }

          return await httpRequest(HttpMethods.post, `admin/uploadkeys?key23=${key23}&tenantId=${currentTenant.siteId}`, {});
        },
        createRoles: async (): Promise<BaseResponse> => {
          return await httpRequest(HttpMethods.post, "admin/create-roles");
        },
        login: async (userName: string, password: string): Promise<UserToken> => {
          return await httpRequest(HttpMethods.post, "token", { userName, password });
        },
      },
      access: {
        getAcl: async (): Promise<AccessEntryInfo[]> => {
          if (!currentTenant) {
            throw new Error(`Api request was not sent, no tenant defined.`);
          }

          return await httpRequest(HttpMethods.get, `access/${currentTenant.siteId}`);
        },
        grant: async (info: AccessEntryInfo): Promise<ValueResponse<AccessEntryInfo>> => {
          if (!currentTenant) {
            throw new Error(`Api request was not sent, no tenant defined.`);
          }

          return await httpRequest(HttpMethods.post, `access/${currentTenant.siteId}`, info);
        },
        update: async (user: AccessEntryInfo): Promise<ValueResponse<AccessEntryInfo>> => {
          if (!currentTenant) {
            throw new Error(`Api request was not sent, no tenant defined.`);
          }
          return await httpRequest(HttpMethods.put, `access/${currentTenant.siteId}`, user);
        },
        revoke: async (info: AccessEntryInfo): Promise<BaseResponse> => {
          if (!currentTenant) {
            throw new Error(`Api request was not sent, no tenant defined.`);
          }

          return await httpRequest(HttpMethods.delete, `access/${currentTenant.siteId}`, info);
        },
      },
      report: {
        get: async (dateTime: Date): Promise<Blob | null> => {
          if (!dateTime) {
            return null;
          }

          return await httpRequest(HttpMethods.get, `report/${dateTime.toISOString()}`, null, true);
        },
      },
      metric: {
        metrics: async (): Promise<AllTimeMetricsResponse | null> => {
          if (!currentTenant) {
            throw new Error(`Api request was not sent, no tenant defined.`);
          }

          return await httpRequest(HttpMethods.get, `metric/metrics?tenantId=${currentTenant.siteId}`);
        },
        metricsByDate: async (args: TimeRangeArgs): Promise<TimeBoundMetricsResponse | null> => {
          if (!currentTenant) {
            throw new Error(`Api request was not sent, no tenant defined.`);
          }

          return await httpRequest(HttpMethods.post, `metric/metricsbydate?tenantId=${currentTenant.siteId}`, args);
        },
        age: async (): Promise<TimeResponse | null> => {
          if (!currentTenant) {
            throw new Error(`Api request was not sent, no tenant defined.`);
          }

          return await httpRequest(HttpMethods.get, `metric/age?tenantId=${currentTenant.siteId}`);
        },
        filters: async (): Promise<ListResponse<MultiFilter> | null> => {
          if (!currentTenant) {
            throw new Error(`Api request was not sent, no tenant defined.`);
          }

          return await httpRequest(HttpMethods.get, `metric/filters?tenantId=${currentTenant.siteId}`);
        },
      },
      list: {
        machinesByReason: async (
          args: SubjectPackageArgs,
          retryOptions?: IRetryOptions
        ): Promise<ListResponse<SubjectStatusAtTime> | null> => {
          if (!currentTenant) {
            throw new Error(`Api request was not sent, no tenant defined.`);
          }

          return await httpRequest(HttpMethods.post, `list/machinesbyreason?tenantId=${currentTenant.siteId}`, args, false, retryOptions);
        },
        machineDetail: async (args: MachineAtTimesArgs): Promise<ListResponse<MachineAtTime> | null> => {
          if (!currentTenant) {
            throw new Error(`Api request was not sent, no tenant defined.`);
          }

          return await httpRequest(HttpMethods.post, `list/machinedetail?tenantId=${currentTenant.siteId}`, args);
        },
        machineSubjectHistory: async (args: MachineSubjectArgs): Promise<ListResponse<SubjectStatusAtTime> | null> => {
          if (!currentTenant) {
            throw new Error(`Api request was not sent, no tenant defined.`);
          }

          return await httpRequest(HttpMethods.post, `list/machinesubjecthistory?tenantId=${currentTenant.siteId}`, args);
        },
        machineRunHistory: async (args: MachineHistoryArgs): Promise<ListResponse<MachineAtTime> | null> => {
          if (!currentTenant) {
            throw new Error(`Api request was not sent, no tenant defined.`);
          }

          return await httpRequest(HttpMethods.post, `list/machinerunhistory?tenantId=${currentTenant.siteId}`, args);
        },
        machinesByStatus: async (args: MachineByStatusArgs, retryOptions?: IRetryOptions): Promise<ListResponse<MachineAtTime> | null> => {
          if (!currentTenant) {
            throw new Error(`Api request was not sent, no tenant defined.`);
          }

          return await httpRequest(HttpMethods.post, `list/machinesbystatus?tenantId=${currentTenant.siteId}`, args, false, retryOptions);
        },
      },
      machine: {
        getIdByName: async (machineName: string): Promise<ScalarResponse<number>> => {
          if (!currentTenant) {
            throw new Error(`Api request was not sent, no tenant defined.`);
          }

          return await httpRequest(HttpMethods.get, `machine/GetIdByName/${currentTenant.siteId}/${machineName}`);
        },
        getMachineDetail: async (
          machineId: number,
          time: Date | null | undefined,
          chartDatePoints: Date[],
          retryOptions?: IRetryOptions
        ): Promise<ScalarResponse<IMachineDetails>> => {
          if (!currentTenant) {
            throw new Error(`Api request was not sent, no tenant defined.`);
          }

          return await httpRequest(
            HttpMethods.post,
            `machine/GetDetails/${currentTenant.siteId}/${machineId}`,
            {
              time: time ? time.toISOString() : null,
              datePoints: chartDatePoints.map((x) => x.toISOString()),
            },
            false,
            retryOptions
          );
        },
        getMachineSubjectHistory: async (
          machineId: number,
          time: Date,
          subject: string,
          retryOptions?: IRetryOptions
        ): Promise<ScalarResponse<IMachineSubjectHistory>> => {
          if (!currentTenant) {
            throw new Error(`Api request was not sent, no tenant defined.`);
          }

          return await httpRequest(
            HttpMethods.get,
            `machine/GetMachineSubjectHistory/${currentTenant.siteId}/${machineId}/${time.toISOString()}/${subject}`,
            undefined,
            false,
            retryOptions
          );
        },
        getRuntimeHistory: async (
          machineId: number,
          page: number,
          pageSize: number,
          order: number,
          retryOptions?: IRetryOptions
        ): Promise<IPagedRuntimeHistoryResponse> => {
          if (!currentTenant) {
            throw new Error(`Api request was not sent, no tenant defined.`);
          }

          return await httpRequest(
            HttpMethods.get,
            `machine/GetRuntimeHistory/${currentTenant.siteId}/${machineId}/${page}/${pageSize}/${order}`,
            undefined,
            false,
            retryOptions
          );
        },
      },
      summary: {
        cachedData: async (retryOptions?: IRetryOptions): Promise<CachedComplianceData | null> => {
          if (!currentTenant) {
            throw new Error(`Api request was not sent, no tenant defined.`);
          }

          return await httpRequest(
            HttpMethods.get,
            `summary/cached/compliancedata?tenantId=${currentTenant.siteId}`,
            undefined,
            false,
            retryOptions
          );
        },
        subjectStatus: async (args: SummarySubjectStatusArgs, retryOptions?: IRetryOptions): Promise<SubjectStatusSummary | null> => {
          if (!currentTenant) {
            throw new Error(`Api request was not sent, no tenant defined.`);
          }

          return await httpRequest(HttpMethods.post, `summary/subjectstatus?tenantId=${currentTenant.siteId}`, args, false, retryOptions);
        },
        subjectStatusSubject: async (args: FilteredTimeArgs, retryOptions?: IRetryOptions): Promise<SubjectStatusSubjectSummary | null> => {
          if (!currentTenant) {
            throw new Error(`Api request was not sent, no tenant defined.`);
          }

          return await httpRequest(
            HttpMethods.post,
            `summary/subjectstatussubject?tenantId=${currentTenant.siteId}`,
            args,
            false,
            retryOptions
          );
        },
        endpointCounts: async (fromTime: Date, retryOptions?: IRetryOptions): Promise<{ totalReporting: number; licensesInUse: number; isSuccess: boolean } | null> => {
          if (!currentTenant) {
            throw new Error(`Api request was not sent, no tenant defined.`);
          }

          return await httpRequest(
            HttpMethods.get,
            `summary/totalreportingendpoints?tenantId=${currentTenant.siteId}&fromTime=${encodeURIComponent(fromTime.toISOString())}`,
            undefined,
            false,
            retryOptions
          );
        },
        subjectStatusReason: async (
          args: FilteredTimeSubjectArgs,
          retryOptions?: IRetryOptions
        ): Promise<SubjectStatusReasonSummary | null> => {
          if (!currentTenant) {
            throw new Error(`Api request was not sent, no tenant defined.`);
          }

          return await httpRequest(
            HttpMethods.post,
            `summary/subjectstatusreason?tenantId=${currentTenant.siteId}`,
            args,
            false,
            retryOptions
          );
        },
      },
      document: {
        getSasToken: async (customer: string, fileName: string): Promise<ValueResponse<string>> => {
          return await httpRequest(HttpMethods.get, `document/sastoken/${customer}/${fileName}`);
        },
      },
      policyEditor: {
        getCompanyIniFile: async (customer: string): Promise<ValueResponse<CompanyIniResponse>> => {
          return await httpRequest(HttpMethods.get, `configfile/company/${customer}`);
        },
        getReplicateIniFile: async (customer: string): Promise<ValueResponse<ReplicateIniResponse>> => {
          return await httpRequest(HttpMethods.get, `configfile/replicate/${customer}`);
        },
        getCommonIniFile: async (): Promise<ValueResponse<CommonIniResponse>> => {
          return await httpRequest(HttpMethods.get, `configfile/common`);
        },
        getPackageMatch: async (customer: string, subject: string): Promise<ListResponse<string>> => {
          return await httpRequest(HttpMethods.get, `configfile/${customer}/packageMatch/${subject}`);
        },
        getPackageIcon: async (customer: string, subject: string): Promise<ValueResponse<string>> => {
          return await httpRequest(HttpMethods.get, `configfile/${customer}/icon/${subject}`);
        },
        getUnconfigured: async (customer: string, subject: string): Promise<ListResponse<UnconfiguredRules>> => {
          return await httpRequest(HttpMethods.get, `configfile/${customer}/configuration/${subject}`);
        },
        getInheritedConfigurations: async (customer: string, matchingPackages: string[]): Promise<ListResponse<UnconfiguredRules>> => {
          return await httpRequest(HttpMethods.post, `configfile/${customer}/configurations/inherited`, matchingPackages);
        },
        generateIniFile: async (
          customer: string,
          args: {
            applicabilityRules: IniInfo[];
            packageConfigSections: PackageConfigSection[];
            clientConfigSection: IniInfo[];
            sitesAndSubnetsSection: IniInfo[];
            systemConfigurations: IniInfo[];
            activeDirectoryDomains: IniInfo[];
            activeDirectoryOUs: IniInfo[];
          }
        ): Promise<Blob | null> => {
          return await httpRequest(HttpMethods.post, `configFile/company/${customer}`, args, true);
        },
        getBlobCompanyIni: async (customer: string): Promise<Blob | null> => {
          return await httpRequest(HttpMethods.get, `configFile/company/blob/${customer}`, null, true);
        },
        verifyLicenseFile: async (customer: string): Promise<ValueResponse<boolean> | null> => {
          return await httpRequest(HttpMethods.get, `configFile/license/${customer}`);
        },
        importLatestCompanyIni: async (tenantId: number, comment: string): Promise<BaseResponse> => {
          return await httpRequest(HttpMethods.post, `CompanyPolicy/policy/importCompanyIni/${tenantId}`, { comment });
        },
        getPolicyVersions: async (tenantId: number): Promise<ListResponse<CompanyPolicyVersion>> => {
          return await httpRequest(HttpMethods.get, `CompanyPolicy/policy/${tenantId}/versions`);
        },
        getPolicyVersion: async (tenantId: number, id: string): Promise<ValueResponse<CompanyPolicyVersionDetail>> => {
          return await httpRequest(HttpMethods.get, `CompanyPolicy/policy/${tenantId}/versions/${id}`);
        },
        copyPolicyVersion: async (tenantId: number, id: string, comment?: string): Promise<ValueResponse<CompanyPolicyVersion>> => {
          return await httpRequest(HttpMethods.post, `CompanyPolicy/policy/${tenantId}/versions/${id}/copy`, { comment: comment || null });
        },
        deletePolicyVersion: async (tenantId: number, id: string, comment?: string): Promise<BaseResponse> => {
          const query = comment ? `?comment=${encodeURIComponent(comment)}` : "";
          return await httpRequest(HttpMethods.delete, `CompanyPolicy/policy/${tenantId}/versions/${id}${query}`);
        },
        getParsedVersion: async (tenantId: number, id: string): Promise<ValueResponse<CompanyIniResponse>> => {
          return await httpRequest(HttpMethods.get, `CompanyPolicy/policy/${tenantId}/versions/${id}/parsed`);
        },
        previewVersion: async (
          tenantId: number,
          id: string,
          companyInfo: {
            applicabilityRules: IniInfo[];
            packageConfigSections: PackageConfigSection[];
            clientConfigSection: IniInfo[];
            sitesAndSubnetsSection: IniInfo[];
            systemConfigurations: IniInfo[];
            activeDirectoryDomains: IniInfo[];
            activeDirectoryOUs: IniInfo[];
            pendingReplicateChanges?: ReplicateIniChanges;
          }
        ): Promise<ValueResponse<PolicyPreviewResponse>> => {
          return await httpRequest(HttpMethods.post, `CompanyPolicy/policy/${tenantId}/versions/${id}/preview`, companyInfo);
        },
        saveVersion: async (
          tenantId: number,
          id: string,
          policyText: string,
          comment?: string,
          pendingReplicateChanges?: ReplicateIniChanges
        ): Promise<BaseResponse> => {
          return await httpRequest(HttpMethods.put, `CompanyPolicy/policy/${tenantId}/versions/${id}`, {
            policyText,
            comment: comment || null,
            pendingReplicateChanges: pendingReplicateChanges ?? { additions: [], removals: [] },
          });
        },
        submitVersion: async (tenantId: number, id: string, comment: string): Promise<BaseResponse> => {
          return await httpRequest(HttpMethods.put, `CompanyPolicy/policy/${tenantId}/versions/${id}/submit`, { comment });
        },
        downloadCompanyIni: async (tenantId: number, id: string): Promise<{ data: Blob; filename: string } | null> => {
          return await httpRequest(HttpMethods.get, `CompanyPolicy/policy/${tenantId}/versions/${id}/download/company`, undefined, true);
        },
        downloadReplicateIni: async (tenantId: number, id: string): Promise<{ data: Blob; filename: string } | null> => {
          return await httpRequest(HttpMethods.get, `CompanyPolicy/policy/${tenantId}/versions/${id}/download/replicate`, undefined, true);
        },
      },
      intune: {
        getTenants: async (tenantId: number): Promise<ListResponse<TenantData>> => {
          return await httpRequest(HttpMethods.get, `IntuneAuth/${tenantId}/tenants`);
        },
        updateTenantDisplayName: async (tenantId: number, entraTenantId: string, tenantDisplayName: string | null): Promise<ItemResponse<TenantData>> => {
          return await httpRequest(HttpMethods.put, `IntuneAuth/${tenantId}/tenants/${entraTenantId}`, { tenantDisplayName });
        },
        getSubjects: async (tenantId: string, customer: string, graphToken?: string): Promise<IntuneData[]> => {
          return await httpRequest(
            HttpMethods.get,
            `intunegraph/${tenantId}/${customer}`,
            undefined,
            false,
            undefined,
            undefined,
            graphToken ? { "X-Graph-Token": graphToken } : undefined
          );
        },
        saveSubject: async (token: string, tenantId: string, customer: string, subject: string): Promise<IntuneSaveResponse> => {
          const raw = await httpRequest(HttpMethods.post, `intunegraph/${tenantId}/${customer}/${subject}`, undefined, false, undefined, undefined, { "X-Graph-Token": token || "", }
          );

          if (!raw || raw.success === false) {
            return { success: false, error: raw?.error };
          }

          // The created row may come back nested under `response` (matching deleteSubject's
          // documented shape) or flat at the top level — accept either.
          return { success: true, response: (raw.response ?? raw) as IntuneData };
        },
        deleteSubject: async (token: string, tenantId: string, customer: string, subject: string): Promise<IntuneDeleteResponse> => {
          const raw = await httpRequest(HttpMethods.delete, `intunegraph/${tenantId}/${customer}/${subject}`, undefined, false, undefined, undefined, { "X-Graph-Token": token || "", }
          );

          if (!raw || raw.success === false) {
            return { success: false, error: raw?.error };
          }

          return { success: true, response: raw.response as IntuneData };
        },
      },
      icons: {
        getIconBySubject: async (subject: string, customer?: string): Promise<ValueResponse<string>> => {
          return await httpRequest(HttpMethods.post, `icon/${subject}`, customer);
        },
        getIconsBySubjects: async (packagesList: string[], customer?: string): Promise<ValueResponse<IPackageIcon[]>> => {
          return await httpRequest(HttpMethods.post, `icon/list`, { packagesList, customer: customer ?? "" });
        },
      },
      encryption: {
        encrypt: async (customerName: string, value: string): Promise<ValueResponse<string>> => {
          return await httpRequest(HttpMethods.post, `encryption/encrypt/${customerName}`, value);
        },
        decrypt: async (customerName: string, encryptedValue: string): Promise<ValueResponse<string>> => {
          return await httpRequest(HttpMethods.post, `encryption/decrypt/${customerName}`, encryptedValue);
        },
      },
      vulnerabilities: {
        getVulnerabilities: async (): Promise<ListResponse<IVulnerability>> => {
          if (!currentTenant) {
            throw new Error(`Api request was not sent, no tenant defined.`);
          }

          return await httpRequest(HttpMethods.get, `vulnerabilityreport?tenantId=${currentTenant?.siteId}`);
        },
      },
      packages: {
        getGlobal: async (): Promise<ListResponse<IPackageInfo>> => {
          return await httpRequest(HttpMethods.get, `packages/global`);
        },
        getSpecifics: async (customers: string[]): Promise<ListResponse<IPackageInfo>> => {
          if (!customers || customers.length === 0) {
            return { items: [], isSuccess: true, failMessage: null, title: null };
          }
          const query = `customers=${encodeURIComponent(customers.join(","))}`;
          return await httpRequest(HttpMethods.get, `packages/specifics?${query}`);
        },
        getInstalled: async (shortname: string): Promise<ListResponse<IPackageInfo>> => {
          return await httpRequest(HttpMethods.get, `packages/installed/${shortname}`);
        },
        getCatalog: async (shortname: string): Promise<ListResponse<IPackageInfo>> => {
          return await httpRequest(HttpMethods.get, `packages/catalog/${shortname}`);
        },
        getCustomers: async (): Promise<ListResponse<IPackageCustomerSummary>> => {
          return await httpRequest(HttpMethods.get, `packages/customers`);
        },
      },
    }),
    [auth, currentTenant, httpRequest]
  );
};

export default useApi;
